import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, inject, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    standalone: false,
    selector: 'por-base-toggle',
    templateUrl: './base-toggle.component.html',
    styleUrls: ['./base-toggle.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BaseToggleComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseToggleComponent implements ControlValueAccessor, OnInit, OnChanges {
    @Input() model: boolean | null | undefined;
    @Input() parentForm!: FormGroup;
    @Input() color = 'primary';
    @Input() tooltip!: string;
    @Input() public formControlName!: string;
    @Output() readonly modelChange = new EventEmitter<boolean>();
    @Input() featureName = '';

    public value!: boolean;
    @Input() disabled = false;
    public changed!: (value: string) => void;
    public touched!: () => void;

    private readonly cdr = inject(ChangeDetectorRef);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['model']) {
            // When model changes from parent, sync to value if not using formControlName
            // or if value hasn't been set yet
            if (this.value === undefined || !this.formControlName) {
                this.value = changes['model'].currentValue;
            }
            this.cdr.detectChanges();
        }
    }

    ngOnInit(): void {
        // Only set default if not using reactive forms (formControlName will set value via writeValue)
        if (!this.formControlName && (this.model === undefined || this.model === null)) {
            this.model = false;
        }
    }
    writeValue(value: boolean): void {
        this.value = value;
        // Don't set this.model here - let the [model] Input binding handle it
        // This prevents conflicts when both formControlName and [model] are used
        this.cdr.detectChanges();
    }

    registerOnChange(fn: () => void): void {
        this.changed = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.touched = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }

    toggle(): void {
        // Toggle based on current visual state (value takes precedence)
        const currentState = this.value !== undefined ? this.value : this.model ?? false;
        const newState = !currentState;

        this.value = newState;
        this.model = newState;
        this.modelChange.emit(newState);

        // Notify reactive form of value change
        if (this.changed) {
            this.changed(newState as unknown as string);
        }

        this.cdr.detectChanges();
    }
}
